import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import YMRSForm from "./YMRSForm";
import { useDispatch } from "react-redux";
import { fetchYmrsAssessment, updateYmrsAssessment } from "../../../store/slices/ymrsAssessmentsSlice";

const EditYMRS = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const [formData, setFormData] = useState({
        caseId,
        patientId,
        patientName: "",
        date: "",
        assessedBy: "",
        elevatedMood: "",
        motorActivity: "",
        sexualInterest: "",
        sleep: "",
        irritability: "",
        speech: "",
        thoughtDisorder: "",
        thoughtContent: "",
        disruptiveBehavior: "",
        appearance: "",
        insight: ""
    });
    const [loading, setLoading] = useState(true);

    // Fetch existing YMRS assessment by ID
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await dispatch(fetchYmrsAssessment(id)).unwrap();

                // Map fetched data to formData
                setFormData({
                    caseId: response.case_Id || "",
                    patientId: response.patient_Id || "",
                    patientName: response.client_name || "",
                    date: response.created_at.split("T")[0] || "",
                    assessedBy: response.assessed_by || "",
                    elevatedMood: response.elevated_mood_score?.toString() || "",
                    motorActivity: response.motor_activity_score?.toString() || "",
                    sexualInterest: response.sexual_interest_score?.toString() || "",
                    sleep: response.sleep_score?.toString() || "",
                    irritability: response.irritability_score?.toString() || "",
                    speech: response.speech_score?.toString() || "",
                    thoughtDisorder: response.thought_disorder_score?.toString() || "",
                    thoughtContent: response.thought_content_score?.toString() || "",
                    disruptiveBehavior: response.disruptive_behavior_score?.toString() || "",
                    appearance: response.appearance_score?.toString() || "",
                    insight: response.insight_score?.toString() || ""
                });
            } catch (error) {
                Swal.fire("Error", "Failed to fetch assessment data", "error");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id, dispatch]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const calculateTotal = () => {
        const fields = [
            "elevatedMood", "motorActivity", "sexualInterest", "sleep",
            "irritability", "speech", "thoughtDisorder", "thoughtContent",
            "disruptiveBehavior", "appearance", "insight"
        ];
        return fields.reduce((sum, field) => sum + Number(formData[field] || 0), 0);
    };

    const isComplete = () => {
        const fields = [
            "elevatedMood", "motorActivity", "sexualInterest", "sleep",
            "irritability", "speech", "thoughtDisorder", "thoughtContent",
            "disruptiveBehavior", "appearance", "insight"
        ];
        return fields.every(field => formData[field] !== "" && formData[field] !== undefined);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isComplete()) {
            Swal.fire("Incomplete", "Please complete all 11 items.", "warning");
            return;
        }

        // Map numeric scores to label values
        const questions = {
            elevatedMood: [
                "Absent",
                "Mildly or possibly increased on questioning",
                "Definite subjective elevation; optimistic; self-confident; cheerful; appropriate to content",
                "Elevated, inappropriate to content; humorous",
                "Euphoric; inappropriate laughter, singing"
            ],
            motorActivity: [
                "Absent",
                "Subjectively increased",
                "Animated; gestures increased",
                "Excessive energy; hyperactive at times; restless (can be calmed)",
                "Motor excitement; continuous hyperactivity (cannot be calmed)"
            ],
            sexualInterest: [
                "Normal; not increased",
                "Mildly or possibly increased",
                "Definite subjective increase on questioning",
                "Spontaneous sexual content; elaborates on sexual matters; hypersexual by self-report",
                "Overt sexual acts (toward patients, staff, or interviewer)"
            ],
            sleep: [
                "Reports no decrease in sleep",
                "Sleeping less than normal amount by up to one hour",
                "Sleeping less than normal by more than one hour",
                "Reports decreased need for sleep",
                "Denies need for sleep"
            ],
            irritability: {
                0: "Absent",
                2: "Subjectively increased",
                4: "Irritable at times during interview; recent episodes of anger or annoyance on ward",
                6: "Frequently irritable during interview; short or curt throughout",
                8: "Hostile, uncooperative; interview impossible"
            },
            speech: {
                0: "No increase",
                2: "Feels talkative",
                4: "Increased rate or amount at times, verbose at times",
                6: "Push; consistently increased rate and amount; difficult to interrupt",
                8: "Pressured; uninterruptible, continuous speech"
            },
            thoughtDisorder: [
                "Absent",
                "Circumstantial; mild distractibility; quick thoughts",
                "Distractible; loses goal of thought; changes topics frequently; racing thoughts",
                "Flight of ideas; tangentiality; difficult to follow; rhyming; echolalia",
                "Incoherent; communication impossible"
            ],
            thoughtContent: {
                0: "Normal",
                2: "Questionable plans; new interests",
                4: "Special project(s); hyper-religious",
                6: "Grandiose or paranoid ideas; ideas of reference",
                8: "Delusions; hallucinations"
            },
            disruptiveBehavior: {
                0: "Absent, cooperative",
                2: "Sarcastic; loud at times, guarded",
                4: "Demanding; threats on ward",
                6: "Threatens interviewer; shouting; interview difficult",
                8: "Assaultive; destructive; interview impossible"
            },
            appearance: [
                "Appropriate dress and grooming",
                "Minimally unkempt",
                "Poorly groomed; moderately disheveled; overdressed",
                "Disheveled; partly clothed; garish makeup",
                "Completely unkempt; decorated; bizarre garb"
            ],
            insight: [
                "Present; admits illness; agrees with need for treatment",
                "Possibly ill",
                "Admits behavior change, but denies illness",
                "Admits possible change in behavior; but denies illness",
                "Denies any behavior change"
            ]
        };

        const payload = {
            case_id: caseId,
            patient_id: patientId,
            client_name: formData.patientName,
            date: formData.date,
            assessed_by: formData.assessedBy,
            elevated_mood_value: questions.elevatedMood[formData.elevatedMood] || "",
            elevated_mood_score: Number(formData.elevatedMood),
            motor_activity_value: questions.motorActivity[formData.motorActivity] || "",
            motor_activity_score: Number(formData.motorActivity),
            sexual_interest_value: questions.sexualInterest[formData.sexualInterest] || "",
            sexual_interest_score: Number(formData.sexualInterest),
            sleep_value: questions.sleep[formData.sleep] || "",
            sleep_score: Number(formData.sleep),
            irritability_value: questions.irritability[formData.irritability] || "",
            irritability_score: Number(formData.irritability),
            speech_value: questions.speech[formData.speech] || "",
            speech_score: Number(formData.speech),
            thought_disorder_value: questions.thoughtDisorder[formData.thoughtDisorder] || "",
            thought_disorder_score: Number(formData.thoughtDisorder),
            thought_content_value: questions.thoughtContent[formData.thoughtContent] || "",
            thought_content_score: Number(formData.thoughtContent),
            disruptive_behavior_value: questions.disruptiveBehavior[formData.disruptiveBehavior] || "",
            disruptive_behavior_score: Number(formData.disruptiveBehavior),
            appearance_value: questions.appearance[formData.appearance] || "",
            appearance_score: Number(formData.appearance),
            insight_value: questions.insight[formData.insight] || "",
            insight_score: Number(formData.insight),
            total_score: calculateTotal()
        };

        try {
            await dispatch(updateYmrsAssessment({ case_id: caseId, assessmentData: payload })).unwrap();
            Swal.fire("Updated!", "YMRS assessment updated successfully.", "success");
            navigate(`/cases/${caseId}/profile`, { state: { patientId, caseId } });
        } catch (error) {
            Swal.fire("Error", error || "Failed to update YMRS assessment", "error");
        }
    };

    if (loading) return <div>Loading...</div>;

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/phase04/ymrs/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <YMRSForm formData={formData} handleChange={handleChange} />
                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4"
                                    >
                                        <i className="ri-edit-2-line me-1"></i>
                                        Update
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditYMRS;
